import { AvailableLanguages } from '@repo/toolkit/language-types'
import { EnumLike, z } from 'zod'

import { t } from '../../../locale/utils'

export type EnumSchemaType<T extends EnumLike> = {
  enumType: T
  fieldName: string
  errorMessage?: string
  language?: AvailableLanguages
} & ({ optional?: boolean; nullish?: undefined } | { nullish?: boolean; optional?: undefined })

export const createEnumSchema = <T extends EnumLike, U extends boolean | undefined = true>({
  enumType,
  fieldName,
  errorMessage,
  optional,
  nullish,
  language
}: EnumSchemaType<T>): U extends true
  ? z.ZodNativeEnum<T>
  : z.ZodOptional<z.ZodNativeEnum<T>> | z.ZodOptional<z.ZodNullable<z.ZodNativeEnum<T>>> => {
  const isOptional = optional ?? false

  const lng = language

  const schema = z.nativeEnum(enumType, {
    required_error: errorMessage ?? t('errors.enum.required', { fieldName, lng }),
    invalid_type_error: t('errors.enum.invalid', { fieldName, lng })
  })

  if (isOptional)
    return schema.optional() as U extends true
      ? z.ZodNativeEnum<T>
      : z.ZodOptional<z.ZodNativeEnum<T>>

  if (nullish)
    return schema.nullish() as U extends true
      ? z.ZodNativeEnum<T>
      : z.ZodOptional<z.ZodNullable<z.ZodNativeEnum<T>>>

  return schema as U extends true ? z.ZodNativeEnum<T> : z.ZodOptional<z.ZodNativeEnum<T>>
}

export type EnumArraySchemaType<T extends EnumLike> = {
  enumType: T
  fieldName: string
  errorMessage?: string
  minimumNumberOfItems?: number
}

export const createEnumArraySchema = <T extends EnumLike>({
  fieldName,
  enumType,
  errorMessage,
  minimumNumberOfItems
}: EnumArraySchemaType<T>) => {
  const baseSchema = createEnumSchema({ fieldName, enumType })
  let arraySchema = z.array(baseSchema, {
    required_error: errorMessage ?? t('errors.enum.list.required', { fieldName })
  })

  if (minimumNumberOfItems) {
    arraySchema = arraySchema.min(minimumNumberOfItems, {
      message: errorMessage ?? t('errors.enum.list.min', { fieldName, minimumNumberOfItems })
    })
  }

  return arraySchema
}
